/**
 * Date and number formatters used across the SPA.
 *
 * This is the single home for every user-facing number/date/currency
 * formatter. If you find yourself writing `.toLocaleString()`,
 * `.toFixed()`, or `$${...}` inline in a component, add (or reuse) a
 * formatter here instead — scattered inline formatting is how the UI
 * drifts into rendering the same value three different ways.
 *
 * Engine: number formatters are backed by `Intl.NumberFormat` (the JS
 * standard) rather than hand-rolled `toLocaleString`/`toFixed` chains.
 * The thin wrappers here add the two things `Intl` does NOT give us:
 *   1. an em-dash (`—`) for null / undefined / NaN, and
 *   2. tolerance for values that arrive as **strings** (wire `Decimal`
 *      fields serialize as strings to preserve precision — see the
 *      "Per-field type overrides" in docs/PATTERNS.md §2). Pass either a
 *      `number` or a numeric string; `parseNumeric` coerces.
 *
 * Text/case transforms live in `lib/text.ts`, URL helpers in `lib/url.ts`.
 */

import type { Commodity } from "@/types/generated/Commodity";
import type { PricingUnit } from "@/types/generated/PricingUnit";
import type { VolumeScale } from "@/types/generated/VolumeScale";
import { GAS_UNIT_LABELS, THERMS_PER_GAS_UNIT, type GasUnit } from "@/types/gas-units";

/** A value that may have arrived as a number or a stringified Decimal. */
export type Numeric = number | string | null | undefined;

/**
 * Coerce a wire value (number or stringified `Decimal`) to a finite
 * number, or `null` when it's absent / non-finite. The gate every
 * number formatter runs first so a `"—"`-on-null contract holds
 * regardless of whether the field came across as `1234` or `"1234.5"`.
 */
export function parseNumeric(v: Numeric): number | null {
  if (v == null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

// ── Intl formatter cache ──────────────────────────────────────────────────────
//
// Constructing an `Intl.NumberFormat` is comparatively expensive, so we
// memoize one instance per distinct option set and reuse it. Call sites
// stay declarative; the cost is paid once per shape.

const numberFormatters = new Map<string, Intl.NumberFormat>();

function intl(options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = JSON.stringify(options);
  let fmt = numberFormatters.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat("en-US", options);
    numberFormatters.set(key, fmt);
  }
  return fmt;
}

// ── Dates ─────────────────────────────────────────────────────────────────────

/**
 * ISO `yyyy-MM-dd` from a `Date`. Wire-format for every date field the
 * server accepts, and the value the date pickers read/write.
 */
export function formatIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Parse an ISO/RFC3339 string and render as `YYYY-MM-DD`. Returns the
 * raw input when the string doesn't parse — keeps the UI from rendering
 * "NaN-NaN-NaN" if a server contract ever drifts.
 *
 * Date-only wire values (our `NaiveDate` `YYYY-MM-DD`) carry no time or
 * zone; routed through `parseDisplayDate` they pin to noon UTC so the
 * calendar day stays stable west of UTC. A bare `new Date("2026-08-01")`
 * parses as UTC midnight, and the local getters below would then shift it
 * to the previous day for any negative-offset timezone (all of the US).
 */
export function formatDate(iso: string): string {
  const d = parseDisplayDate(iso);
  if (!d) return iso;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Parse an ISO/RFC3339 string and render as `YYYY-MM-DD HH:MM`. Same
 * fallback semantics as `formatDate`.
 */
export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day} ${hh}:${mm}`;
}

/**
 * Render an RFC3339 instant in the viewer's own time zone with the zone
 * abbreviation — e.g. `2026-04-30T21:00:00Z` → `Apr 30, 2026, 5:00 PM EDT`
 * for a New York viewer. The browser supplies the zone; no zone logic here.
 * Returns the raw input if it doesn't parse (wire drift never renders as
 * "Invalid Date").
 */
export function formatInstantWithZone(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return intlDate({
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(d);
}

/**
 * Render an RFC3339 instant as a short relative age — "just now", "5m ago",
 * "3h ago", "2d ago" — falling back to an absolute `formatDate` once it's a
 * week old, because "37d ago" is harder to place than "Jul 27".
 *
 * For recency-ordered lists (the "Where you left off" recents widget, activity
 * feeds) where the exact instant matters less than "how stale is this". Pair it
 * with a `title` carrying `formatInstantWithZone` when the precise time is
 * worth a hover.
 *
 * Returns the raw input if it doesn't parse, like every other formatter here.
 * A future instant (clock skew between server and browser) reads "just now"
 * rather than "in 3 minutes" — this labels the past by construction.
 */
export function formatRelativeTime(iso: string, now = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const seconds = Math.floor((now.getTime() - d.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(iso);
}

/**
 * The viewer's own time-zone abbreviation right now (e.g. "EDT", "PST"),
 * straight from the browser. Used as the label next to local time inputs.
 */
export function viewerZoneAbbreviation(): string {
  const part = intlDate({ timeZoneName: "short" })
    .formatToParts(new Date())
    .find((p) => p.type === "timeZoneName");
  return part?.value ?? "";
}

/**
 * Format a date as "Wed Apr 22 2026" — matches current_date_str() in Rust.
 * Topbar "as of" indicator.
 */
export function formatCurrentDate(d = new Date()): string {
  const weekday = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()];
  const month = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ][d.getMonth()];
  return `${weekday} ${month} ${d.getDate()} ${d.getFullYear()}`;
}

/**
 * Parse an ISO/RFC3339 (or bare `yyyy-MM-dd`) string into a `Date`,
 * pinning date-only strings to **noon UTC** so the calendar day stays
 * stable across browser timezones (a PT/MT user viewing a midnight-UTC
 * date would otherwise see yesterday). Returns `null` when it doesn't
 * parse. Shared by every long-form date formatter below.
 */
function parseDisplayDate(iso: string): Date | null {
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  const d = new Date(dateOnly ? `${iso}T12:00:00Z` : iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

const dateFormatters = new Map<string, Intl.DateTimeFormat>();

function intlDate(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = JSON.stringify(options);
  let fmt = dateFormatters.get(key);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat("en-US", options);
    dateFormatters.set(key, fmt);
  }
  return fmt;
}

/**
 * Render an ISO `yyyy-MM-dd` (or RFC3339) string as a customer-facing
 * long-form date — e.g. `2026-12-31` → `Dec 31, 2026`.
 *
 * Returns the raw input string if parsing fails so a wire-shape drift
 * never renders as "Invalid Date" on a customer-facing slide.
 */
export function formatLongDate(iso: string): string {
  const d = parseDisplayDate(iso);
  if (!d) return iso;
  return intlDate({ year: "numeric", month: "short", day: "numeric" }).format(d);
}

/** `2026-04-22` → `Apr 2026` (month + year). Raw input on parse failure. */
export function formatMonthYear(iso: string): string {
  const d = parseDisplayDate(iso);
  if (!d) return iso;
  return intlDate({ year: "numeric", month: "short" }).format(d);
}

/**
 * `2026-04-22` → `Apr 26` (month + two-digit year). For a period picker's
 * chips, beside "Q1 27" and "Cal 27". Raw input on parse failure.
 */
export function formatMonthYearShort(iso: string): string {
  const d = parseDisplayDate(iso);
  if (!d) return iso;
  return intlDate({ year: "2-digit", month: "short" }).format(d);
}

/**
 * `2026-04-22` → `Apr` (the month alone). For a seasonal axis whose points
 * share a stand-in year, where the year would be noise. Raw input on parse
 * failure.
 */
export function formatMonthShort(iso: string): string {
  const d = parseDisplayDate(iso);
  if (!d) return iso;
  return intlDate({ month: "short" }).format(d);
}

/** `2026-04-22` → `Apr 22` (month + day, no year). Raw input on parse failure. */
export function formatMonthDay(iso: string): string {
  const d = parseDisplayDate(iso);
  if (!d) return iso;
  return intlDate({ month: "short", day: "numeric" }).format(d);
}

/** `2026-04-22` → `Wed Apr 22` (weekday + month + day). Raw input on parse failure. */
export function formatWeekdayMonthDay(iso: string): string {
  const d = parseDisplayDate(iso);
  if (!d) return iso;
  return intlDate({ weekday: "short", month: "short", day: "numeric" }).format(d);
}

/** `2026-09-16` → `Wednesday` (the full weekday). Raw input on parse
 *  failure. */
export function formatWeekdayLong(iso: string): string {
  const d = parseDisplayDate(iso);
  if (!d) return iso;
  return intlDate({ weekday: "long" }).format(d);
}

/**
 * Render an RFC3339 instant as its time of day in the viewer's own zone, with
 * the zone named — `5:20 PM EDT`. For a table whose date sits in its own
 * column. `—` for null; the raw input when it doesn't parse.
 */
export function formatTimeWithZone(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return intlDate({ hour: "numeric", minute: "2-digit", timeZoneName: "short" }).format(d);
}

/**
 * A wall-clock time of day, 24-hour `HH:mm`, as people read it:
 * `09:30` → `9:30 AM`, `21:00` → `9:00 PM`. Not an instant, so no zone. The
 * raw input when it isn't `HH:mm`.
 */
export function formatClockTime(hhmm: string): string {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!m) return hhmm;
  const h = Number(m[1]);
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${m[2]} ${h >= 12 ? "PM" : "AM"}`;
}

/**
 * Render an RFC3339 instant in a NAMED zone, not the viewer's, with that
 * zone's abbreviation at the instant — `2026-10-16T13:30:00Z` in
 * `America/New_York` → `Fri, Oct 16, 2026, 9:30 AM EDT`. For a scheduled
 * report's sends, which happen in the schedule's zone wherever the reader is.
 * Falls back to the viewer's zone for a zone the browser doesn't know, and to
 * the raw input when it doesn't parse.
 */
export function formatInstantInZone(iso: string, timeZone: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return intlDate({
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
      timeZone,
    }).format(d);
  } catch {
    return formatInstantWithZone(iso);
  }
}

/**
 * An instant in a NAMED zone split for a two-line display:
 * `{ day: "Thu, Oct 15", time: "11:30 AM EDT" }`. The year shows only when it
 * isn't this year. Raw input as `day` when it doesn't parse.
 */
export function formatInstantInZoneParts(
  iso: string,
  timeZone: string,
  now = new Date(),
): { day: string; time: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { day: iso, time: "" };
  try {
    const sameYear =
      intlDate({ year: "numeric", timeZone }).format(d) === intlDate({ year: "numeric", timeZone }).format(now);
    return {
      day: intlDate({
        weekday: "short",
        month: "short",
        day: "numeric",
        ...(sameYear ? {} : { year: "numeric" }),
        timeZone,
      }).format(d),
      time: intlDate({ hour: "numeric", minute: "2-digit", timeZoneName: "short", timeZone }).format(d),
    };
  } catch {
    return { day: formatInstantWithZone(iso), time: "" };
  }
}

/**
 * How far ahead an instant is — "in 5m", "in 3h", "in 2d" — the mirror of
 * `formatRelativeTime`. A past instant reads "now". Raw input on parse
 * failure.
 */
export function formatTimeUntil(iso: string, now = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const minutes = Math.round((d.getTime() - now.getTime()) / 60_000);
  if (minutes < 1) return "now";
  if (minutes < 60) return `in ${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `in ${hours}h`;
  return `in ${Math.round(hours / 24)}d`;
}

/** `2026-04-22` → `2026` (year only). Raw input on parse failure. */
export function formatYear(iso: string): string {
  const d = parseDisplayDate(iso);
  if (!d) return iso;
  return String(d.getFullYear());
}

// ── Durations ─────────────────────────────────────────────────────────────────

/** Average days per month (365.25 / 12) — the month divisor for long spans. */
const DAYS_PER_MONTH = 30.4375;

/**
 * Elapsed time since an RFC3339 instant, rendered as a single coarse unit —
 * HubSpot's "Time in current Stage" column. Rolls up through the largest
 * sensible unit so a long stage never reads as a giant minute count: minutes
 * under an hour, hours under a day, days under a month, then months (one
 * decimal) — e.g. `45 minutes`, `18 hours`, `6 days`, `15.1 months`. Returns
 * `—` when the input is absent, unparseable, or in the future (clock skew).
 *
 * The value is derived from `now` at render time, so it advances on each
 * re-render without a refetch — the deal's `state_entered_at` is the only
 * server input.
 */
export function formatStageDuration(iso: string | null | undefined): string {
  if (!iso) return "—";
  const start = new Date(iso);
  if (Number.isNaN(start.getTime())) return "—";
  const seconds = (Date.now() - start.getTime()) / 1000;
  if (seconds < 0) return "—";
  if (seconds < 60) return "less than a minute";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} ${days === 1 ? "day" : "days"}`;
  return `${formatDecimal(days / DAYS_PER_MONTH)} months`;
}

/**
 * A short elapsed duration (e.g. a logged call's length) as `Xm Ys` /
 * `Xh Ym`, from milliseconds — HubSpot's `hs_call_duration` is in ms.
 * Under a minute renders as `Ns`. Returns `—` for null/undefined/NaN or a
 * negative value.
 */
export function formatDurationMs(ms: Numeric): string {
  const n = parseNumeric(ms);
  if (n === null || n < 0) return "—";
  const totalSeconds = Math.round(n / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (totalMinutes < 60) return seconds > 0 ? `${totalMinutes}m ${seconds}s` : `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}

// ── Addresses ───────────────────────────────────────────────────────────────

/** Address fields to assemble into a single display line. */
export interface AddressParts {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
}

/**
 * Joins address parts into a single " • " separated string. Returns null
 * when every part is blank. City/state/zip collapse into one comma-joined
 * sub-chunk so the separator between logical lines stays consistent.
 */
export function formatAddress(parts: AddressParts): string | null {
  const { address1, address2, city, state, zip, country } = parts;
  const cityLine = [city, state, zip].filter(Boolean).join(", ");
  const bits = [address1, address2, cityLine, country].filter(
    (b) => b && b.length > 0,
  );
  if (bits.length === 0) return null;
  return bits.join(" • ");
}

/**
 * Joins address parts into a newline-separated string suitable for
 * rendering inside a `whitespace-pre-line` element. Returns null when
 * every part is blank. Same logical grouping as `formatAddress` (the
 * city/state/zip line is one chunk) but each chunk gets its own line —
 * better for vertical real estate like a sidebar property.
 */
export function formatAddressMultiline(parts: AddressParts): string | null {
  const { address1, address2, city, state, zip, country } = parts;
  const cityLine = [city, state, zip].filter(Boolean).join(", ");
  const lines = [address1, address2, cityLine, country].filter(
    (b) => b && b.length > 0,
  );
  if (lines.length === 0) return null;
  return lines.join("\n");
}

// ── Money ─────────────────────────────────────────────────────────────────────

/**
 * Whole-dollar currency display, e.g. `$1,234`. Returns `—` for null,
 * undefined, or NaN. Rounds to the nearest dollar; use `formatMoneyPrecise`
 * when sub-dollar precision matters or `formatMoneyCompact` for dense
 * headline tiles.
 */
export function formatMoney(v: Numeric): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  return intl({
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

/**
 * Two-decimal currency display, e.g. `$1,234.50`. Returns `—` for
 * null/undefined/NaN. Matches the Rust server's `fmt_dollars` style so
 * inline UX strings and server-sent messages read consistently.
 */
export function formatMoneyPrecise(v: Numeric): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  return intl({
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

/**
 * Currency padded AND truncated to exactly `digits` fractional places —
 * `formatMoneyFixed("41.2", 2)` → `"$41.20"`. The currency twin of
 * `formatFixedDecimal`: use it where the precision is a property of the
 * surface rather than of the value, so every number on that surface lines
 * up at the same decimal place.
 *
 * The motivating case is the RFQ market-context section (AB#8066), whose
 * charts must quote every price at one precision — `MARKET_PRICE_DECIMALS`
 * in `pages/energy-advisor/rfq/components/market-context/units.ts` — across
 * independently-written charts. `formatMoneyPrecise` is the same thing with
 * `digits` frozen at 2; reach for that one unless the caller owns the
 * precision. Returns `—` for null/undefined/NaN.
 */
export function formatMoneyFixed(v: Numeric, digits: number): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  return intl({
    style: "currency",
    currency: "USD",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(n);
}

/**
 * Signed two-decimal currency, e.g. `+$1.23`, `−$4.56`, `$0.00`. Uses
 * the typographic minus sign (U+2212) for negatives to match the rest
 * of the analytics surfaces. Returns `—` for null/undefined/NaN. Use
 * for deltas where the direction matters (a billed-vs-modeled gap).
 */
export function formatMoneySigned(v: Numeric): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  return `${sign}${formatMoneyPrecise(Math.abs(n))}`;
}

/**
 * Compact currency for dense legend rows / bar labels / KPI tiles —
 * `$1.5M`, `$24k`, `$980`. Drops cents on the compact tiers; small
 * amounts (< $10k) fall through to whole-dollar. Returns `—` for
 * null/undefined/NaN.
 *
 * The `$1.5M` / `$24k` thresholds (1M and 10k) and lowercase `k` /
 * uppercase `M` are the established house style — `Intl`'s built-in
 * `notation: "compact"` produces `$1.5M`/`$24K` (uppercase K, and a
 * cutoff we don't control), so we keep the hand-tiered version to
 * preserve the exact current rendering.
 */
export function formatMoneyCompact(v: Numeric): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 10_000) return `$${Math.round(n / 1_000)}k`;
  return formatMoney(n);
}

// ── Plain numbers ───────────────────────────────────────────────────────────

/**
 * Locale-formatted integer display (commas, no fraction), e.g.
 * `1,234`. Returns `—` for null, undefined, or NaN.
 */
export function formatNumber(v: Numeric): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  return intl({ maximumFractionDigits: 0 }).format(n);
}

/**
 * Locale-formatted number keeping up to `maxDecimals` fractional digits
 * (default 1), e.g. `2.5`, `1,234.5`. Returns `—` for null/undefined/NaN.
 * Use for usage/intensity values where a fraction matters but the unit
 * suffix is applied by the caller (or one of the unit formatters below).
 */
export function formatDecimal(v: Numeric, maxDecimals = 1): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  return intl({ maximumFractionDigits: maxDecimals }).format(n);
}

/**
 * Locale-formatted number whose PRECISION SCALES WITH MAGNITUDE: whole numbers
 * at 10+, one decimal from 1–10, two below 1. Returns `—` for
 * null/undefined/NaN, and an exact `0` stays `"0"`.
 *
 * For headline stat tiles where the same field spans orders of magnitude and a
 * fixed precision lies at one end of the range. The Portfolio Analyzer's load
 * vitals are the motivating case: `formatDecimal(annual_mwh, 0)` rendered a real
 * 0.42 MWh/yr account as `"0"`, which read as "no data" — and sat beside a live
 * "77% load factor", so the tile row contradicted itself. Small commercial
 * meters genuinely run at fractions of a MWh/yr and fractions of a kW.
 *
 * Prefer this over `formatDecimal(v, 2)` when large values are also expected:
 * a fixed two decimals turns `4,548` into `4,548.00`, which is noise.
 */
export function formatMagnitude(v: Numeric): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  const a = Math.abs(n);
  if (a === 0) return "0";
  const digits = a >= 10 ? 0 : a >= 1 ? 1 : 2;
  return intl({ maximumFractionDigits: digits }).format(n);
}

/**
 * Locale-formatted number padded AND truncated to exactly `digits`
 * fractional places, e.g. `formatFixedDecimal("0.079", 5)` → `"0.07900"`.
 * Unlike `formatDecimal` (max digits, no padding) this always shows
 * `digits` decimals — use it where a fixed precision is the point, e.g.
 * the deal card's Price at full entered precision (AB#7376). Returns `—`
 * for null/undefined/NaN.
 */
export function formatFixedDecimal(v: Numeric, digits: number): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  return intl({
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(n);
}

/**
 * File size in `KB` / `MB` with one decimal, e.g. `512 B`, `4.0 KB`,
 * `12.3 MB`. For upload previews. Returns `—` for null/undefined/NaN.
 */
export function formatBytes(v: Numeric): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  if (n < 1024) return `${Math.round(n)} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Energy rates ────────────────────────────────────────────────────────────

// ── Configurable electric pricing unit (AB#7103) ────────────────────────────
//
// Electric rates are stored canonically in `$/kWh`. The brokerage configures a
// system-level *display* unit (`$/kWh`, `¢/kWh`, or `$/MWh`) on
// `core.tenant_settings`; it arrives on `/api/session` and `<AppShell>` pushes
// it here once, before any descendant renders, via `setActivePricingUnit`.
//
// A module-level default (rather than threading a unit arg through every
// `formatRate` call site) keeps the ~30 existing callers untouched AND keeps
// public/unauthenticated surfaces — which render outside the session shell and
// never call the setter — on the canonical `$/kWh`. Mirrors the Rust
// `PricingUnit::format` exactly so server- and client-rendered rates agree.

let activePricingUnit: PricingUnit = "dollars_per_kwh";

/**
 * Set the system-level electric pricing display unit. Called once by
 * `<AppShell>` after the session resolves (and again if the admin changes the
 * setting and the session refetches). Idempotent.
 */
export function setActivePricingUnit(unit: PricingUnit): void {
  activePricingUnit = unit;
}

/** The configured system pricing unit (see `setActivePricingUnit`). For
 *  surfaces whose own unit control overrides it (the RFQ Results
 *  kWh/MWh toggle), read this as the per-kWh-position baseline. */
export function getActivePricingUnit(): PricingUnit {
  return activePricingUnit;
}

// Per-unit decimal precision for the rate formatters. Each unit's precision is
// chosen so converting a canonical `$/kWh` value loses no significant digits:
// `¢/kWh` shifts ×100 (2 fewer decimals), `$/MWh` shifts ×1000 (so it needs
// fewer decimals than `$/kWh` but we keep one extra for bid precision).
type RatePrecision = Record<PricingUnit, number>;

// `formatRate` / `formatRateBare` — display precision.
const STANDARD_PRECISION: RatePrecision = {
  dollars_per_kwh: 4,
  cents_per_kwh: 2,
  dollars_per_mwh: 2,
};

// `formatBidRate` — the higher precision the RFQ auction compares bids at.
//
// `$/MWh` is the exception: a bid stated per MWh is read and quoted as a whole
// dollar figure, so `$97.000` was three digits of noise on every rate cell, the
// Lowest Price tile and the bid-spread track (AB#8234). Per kWh and per cent
// keep their extra digit — there the digits are the difference between two
// bids.
const BID_PRECISION: RatePrecision = {
  dollars_per_kwh: 5,
  cents_per_kwh: 3,
  dollars_per_mwh: 0,
};

/**
 * Convert a canonical `$/kWh` value into `unit` and format its MAGNITUDE with
 * the currency mark only — `$0.0739`, `7.39¢`, `$73.90`. No per-unit
 * denominator (`/kWh`); callers that need it append `rateDenominator` or use
 * `formatRate` (which bakes the full suffix in).
 */
function rateMagnitude(n: number, unit: PricingUnit, dp: RatePrecision): string {
  switch (unit) {
    case "cents_per_kwh":
      return `${(n * 100).toFixed(dp.cents_per_kwh)}¢`;
    case "dollars_per_mwh":
      return `$${(n * 1000).toFixed(dp.dollars_per_mwh)}`;
    case "dollars_per_kwh":
    default:
      return `$${n.toFixed(dp.dollars_per_kwh)}`;
  }
}

/**
 * The denominator label for the configured (or given) unit — `kWh` for
 * `$/kWh` and `¢/kWh`, `MWh` for `$/MWh`. Use this next to a `formatRateBare`
 * / `formatBidRate` value when a column header or sub-label carries the unit
 * instead of the value itself, so the denominator tracks the active unit.
 */
export function rateDenominator(
  unit: PricingUnit = activePricingUnit,
): "kWh" | "MWh" {
  return unit === "dollars_per_mwh" ? "MWh" : "kWh";
}

/**
 * How many of a unit's magnitude make one `$/MWh` (AB#8335).
 *
 * `$/MWh` is the storage unit for the deal's pass-through price inputs,
 * while the rest of the app renders rates in the user's configured display
 * unit. These two helpers move a value between the two so an input field
 * can be labelled and edited in the unit the user picked without changing
 * what is stored.
 */
const PER_MWH_DIVISOR: Record<PricingUnit, number> = {
  dollars_per_kwh: 1000,
  cents_per_kwh: 10,
  dollars_per_mwh: 1,
};

/** A stored `$/MWh` value as the given display unit's magnitude. */
export function perMwhToUnit(
  perMwh: number,
  unit: PricingUnit = activePricingUnit,
): number {
  return perMwh / PER_MWH_DIVISOR[unit];
}

/** A display-unit magnitude back to the stored `$/MWh`. */
export function unitToPerMwh(
  value: number,
  unit: PricingUnit = activePricingUnit,
): number {
  return value * PER_MWH_DIVISOR[unit];
}

/**
 * The electric rate-unit label (`$/kWh`, `¢/kWh`, `$/MWh`) for the configured
 * (or given) display unit. Use it next to a captured-as-typed rate input/label
 * (the value is stored verbatim in this unit — no conversion), so the unit
 * shown tracks the brokerage's `default_pricing_unit`.
 */
export function electricRateUnitLabel(
  unit: PricingUnit = activePricingUnit,
): "$/kWh" | "¢/kWh" | "$/MWh" {
  switch (unit) {
    case "cents_per_kwh":
      return "¢/kWh";
    case "dollars_per_mwh":
      return "$/MWh";
    case "dollars_per_kwh":
    default:
      return "$/kWh";
  }
}

/** What a canonical `$/kWh` rate is multiplied by to reach `unit`. */
function rateFactor(unit: PricingUnit): number {
  switch (unit) {
    case "cents_per_kwh":
      return 100;
    case "dollars_per_mwh":
      return 1000;
    case "dollars_per_kwh":
    default:
      return 1;
  }
}

/**
 * Scale by a power of ten without the binary-float residue. `0.0666 * 100` is
 * `6.660000000000001` in IEEE 754, which lands verbatim in a rate input and
 * then in the database. Rates are stored at five decimals, so rounding to eight
 * cannot lose a stored digit at any of the three display scales.
 */
function shiftDecimal(n: number, factor: number): string {
  const scaled = Number((n * factor).toFixed(8));
  return String(scaled);
}

/**
 * A rate the user has not filled in, as distinct from one they set to zero.
 *
 * `parseNumeric` reads `""` as `0`, because `Number("")` is `0` — right for a
 * formatter, wrong here. A rate field is a controlled string where `""` means
 * unset, so an empty field would otherwise convert to a stored `0` and the
 * server would reject the save as "Current rate must be greater than 0."
 */
function parseRate(v: Numeric): number | null {
  if (typeof v === "string" && v.trim() === "") return null;
  return parseNumeric(v);
}

/**
 * A canonical `$/kWh` rate as the NUMBER a rate INPUT should hold, in the
 * configured display unit. The formatters above produce display strings; this
 * is for an editable field, where the value has to round-trip.
 *
 * Returns `""` for a null/blank/unparseable input so it drops straight into
 * form state. Electric only: gas rates are `$/therm` in both directions.
 */
export function rateToDisplayUnit(
  v: Numeric,
  unit: PricingUnit = activePricingUnit,
): string {
  const n = parseRate(v);
  if (n === null) return "";
  return shiftDecimal(n, rateFactor(unit));
}

/**
 * The inverse of [`rateToDisplayUnit`]: what a rate input holding a value in
 * the configured display unit is worth in canonical `$/kWh`. Apply on the way
 * to the wire so storage stays canonical, as it is for every other rate.
 *
 * Returns `null` for an empty field, which is what the wire carries for a rate
 * the user left blank.
 */
export function rateToCanonical(
  v: Numeric,
  unit: PricingUnit = activePricingUnit,
): string | null {
  const n = parseRate(v);
  if (n === null) return null;
  return shiftDecimal(n, 1 / rateFactor(unit));
}

/**
 * Short label for the configured (or given) rate unit — `$/kWh`,
 * `¢/kWh`, or `$/MWh`. Use on a toggle / axis caption that names the
 * unit (the value itself goes through `formatBidRate` / `formatRateBare`,
 * which carry only the currency mark). Tracks the active display unit.
 */
export function pricingUnitLabel(
  unit: PricingUnit = activePricingUnit,
): "$/kWh" | "¢/kWh" | "$/MWh" {
  switch (unit) {
    case "cents_per_kwh":
      return "¢/kWh";
    case "dollars_per_mwh":
      return "$/MWh";
    default:
      return "$/kWh";
  }
}

/**
 * Format a canonical `$/kWh` rate into a specific display unit with its full
 * suffix: `$0.0739/kWh`, `7.39¢/kWh`, `$73.90/MWh`. Returns `—` for
 * null/undefined/NaN.
 */
export function formatRateInUnit(v: Numeric, unit: PricingUnit): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  return `${rateMagnitude(n, unit, STANDARD_PRECISION)}/${rateDenominator(unit)}`;
}

/**
 * Energy rate rendered in the configured system display unit — `$0.0739/kWh`
 * (default), `7.39¢/kWh`, or `$73.90/MWh`. Input is always the canonical
 * `$/kWh` wire value; conversion is presentation-only. Returns `—` for null,
 * undefined, or NaN.
 */
export function formatRate(v: Numeric): string {
  return formatRateInUnit(v, activePricingUnit);
}

/**
 * Bid / auction rate in the given unit (default: the configured unit) at
 * auction precision — 5 decimals for `$/kWh` (`$0.07390`), 3 for `¢/kWh`
 * (`7.390¢`) / `$/MWh` (`$73.900`). Currency mark only, NO `/kWh`
 * denominator — the surrounding column/label supplies it (use
 * `rateDenominator` so that label tracks the same unit). Returns `—` for
 * null/undefined/NaN.
 */
export function formatBidRate(v: Numeric, unit?: PricingUnit): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  return rateMagnitude(n, unit ?? activePricingUnit, BID_PRECISION);
}

/**
 * Rate in the configured unit with the currency mark but NO denominator
 * suffix — `$0.0739`, `7.39¢`, `$73.90` — for rate cells where a column
 * header or sub-label already carries the denominator (pair with
 * `rateDenominator` so it tracks the active unit). Use `formatRate` when you
 * want the baked-in `/kWh` suffix, `formatBidRate` for auction precision.
 * Returns `—` for null/undefined/NaN.
 */
export function formatRateBare(v: Numeric): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  return rateMagnitude(n, activePricingUnit, STANDARD_PRECISION);
}

/**
 * Matrix price at 5 decimals with the currency mark only — `$0.05493`.
 *
 * Five decimals because a matrix price is a number the user quotes: at 4
 * decimals `$0.054934` and `$0.054925` both render `$0.0549`, and the
 * supplier's own sheet shows the digit we dropped.
 *
 * No unit conversion. A matrix row's `price` is already stated in that
 * row's own `price_uom` (`$/kWh`, `$/MWh`, `$/Dth`, `$/therm`, …), so the
 * configurable electric display unit must not scale it; the call site
 * appends the row's `price_uom` as the suffix. Returns `—` for
 * null/undefined/NaN.
 */
export function formatMatrixPrice(v: Numeric): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  return `$${n.toFixed(5)}`;
}

/**
 * Matrix price with its own unit as the suffix — `$0.05300/kWh`,
 * `5.30000¢/kWh`. Dollar units lead with the currency mark; cents units
 * carry `¢` inside the suffix, so no `$` is prepended. Same 5-decimal
 * precision as `formatMatrixPrice`. Returns `—` for null/undefined/NaN.
 */
export function formatMatrixPriceWithUom(v: Numeric, uom: string): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  if (uom.startsWith("¢")) return `${n.toFixed(5)}${uom}`;
  return `$${n.toFixed(5)}${uom.replace("$", "")}`;
}

/**
 * Demand rate in `$XX.XX/kW` with 2 decimals — `$38.82/kW`. Demand charges
 * are $/kW of peak capacity and are NEVER blended into an energy $/kWh
 * rate (invariant — they don't ride the configurable pricing unit). Used
 * by the tariff analysis' schedule ladder + switch-candidate copy.
 * Returns `—` for null/undefined/NaN.
 */
export function formatKwRate(v: Numeric): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  return `$${n.toFixed(2)}/kW`;
}

// ── User gas display unit (AB#8545) ─────────────────────────────────────────
//
// Gas volumes are stored in therms and gas rates in `$/therm`. A user may pick
// a display unit for gas in Preferences — Ccf, Mcf, Dth, therms or MMBtu — and
// `<AppShell>` pushes it here, the way it pushes the electric pricing unit.
// `null` means the user never chose: every gas helper then keeps its own
// long-standing unit (therms for usage and rates, Dth for the sales-facing
// volume scale), so nothing moves for a user who has not picked one.
//
// Surfaces that show a unit the value was STATED in keep it: a deal's own
// price UOM, a supplier's matrix price UOM, a CCA strike price per Ccf, the
// Enbridge tariff's Mcf, a wholesale hub's $/MMBtu, and every input field.
// The electric pricing unit leaves the same surfaces alone.

let activeGasUnit: GasUnit | null = null;

/** Set the signed-in user's gas display unit. Called by `<AppShell>`;
 *  `null` restores each surface's own unit. */
export function setActiveGasUnit(unit: GasUnit | null): void {
  activeGasUnit = unit;
}

/** The user's gas display unit, or `null` when they never chose. */
export function getActiveGasUnit(): GasUnit | null {
  return activeGasUnit;
}

/** How a gas unit is written beside a value — `Mcf`, `Dth`, `therms`. */
export function gasUnitLabel(unit: GasUnit): string {
  return GAS_UNIT_LABELS[unit];
}

/** The unit a gas VOLUME in therms shows in: the user's, else `therms`. */
export function gasVolumeUnitLabel(): string {
  return activeGasUnit ? GAS_UNIT_LABELS[activeGasUnit] : "therms";
}

/** The denominator a gas RATE in `$/therm` shows per: the user's unit, else
 *  `therm`. A rate reads "per therm", never "per therms". */
export function gasRateDenominator(unit: GasUnit | null = activeGasUnit): string {
  if (!unit || unit === "therms") return "therm";
  return GAS_UNIT_LABELS[unit];
}

/** A therms quantity in `unit`. */
export function thermsToGasUnit(therms: number, unit: GasUnit): number {
  return therms / THERMS_PER_GAS_UNIT[unit];
}

/** A `$/therm` rate in `$/unit` — a larger unit costs proportionally more. */
export function perThermToGasUnit(rate: number, unit: GasUnit): number {
  return rate * THERMS_PER_GAS_UNIT[unit];
}

/**
 * A gas volume stored in therms, in the user's gas unit with its label —
 * `12,000 therms`, `1,156.1 Mcf`. Whole therms stay whole; a converted
 * figure keeps one decimal. Returns `—` for null/undefined/NaN.
 */
export function formatGasVolume(v: Numeric): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  if (!activeGasUnit || activeGasUnit === "therms") return `${formatNumber(n)} therms`;
  return `${formatDecimal(thermsToGasUnit(n, activeGasUnit))} ${GAS_UNIT_LABELS[activeGasUnit]}`;
}

/** {@link formatGasVolume} without the unit, for a column whose header
 *  carries it. */
export function formatGasVolumeBare(v: Numeric): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  if (!activeGasUnit || activeGasUnit === "therms") return formatDecimal(n);
  return formatDecimal(thermsToGasUnit(n, activeGasUnit));
}

/**
 * A metered usage figure with its unit: electric in kWh, gas in the user's
 * gas unit (therms by default). The Accounts grids' Annual Usage and the
 * client tabs' usage cells. Returns `—` for null/undefined/NaN.
 */
export function formatUsage(v: Numeric, commodity: Commodity): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  if (commodity === "electric") return `${formatNumber(n)} kWh`;
  return formatGasVolume(n);
}

/**
 * Gas supply rate with 4 decimals, per the user's gas unit — `$0.5123/therm`
 * by default, `$5.1230/Dth` for a Dth user. The configurable ELECTRIC unit
 * (`$/kWh`, `¢/kWh`, `$/MWh`) never touches gas. Use this on mixed-commodity
 * surfaces to keep gas rows correct while electric rows go through
 * `formatRate`. Returns `—` for null/undefined/NaN.
 */
export function formatRatePerTherm(v: Numeric): string {
  return formatGasRateIn(v, activeGasUnit);
}

/** A `$/therm` rate shown per `unit` (or per therm for `null`), 4 decimals —
 *  the Preferences preview renders a unit before the user commits to it.
 *  Returns `—` for null/undefined/NaN. */
export function formatGasRateIn(v: Numeric, unit: GasUnit | null): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  const shown = unit ? perThermToGasUnit(n, unit) : n;
  return `$${shown.toFixed(4)}/${gasRateDenominator(unit)}`;
}

// ── Commodity-aware rate helpers ───────────────────────────────────────────────
//
// The configurable display unit (`$/kWh` / `¢/kWh` / `$/MWh`) is an ELECTRIC
// concept; gas rates are always `$/therm`. Mixed-commodity surfaces (the RFQ
// results) carry the RFQ's `Commodity` and route every rate through these so a
// gas RFQ never renders "Rate (kWh)" or a kWh-scaled magnitude.

/**
 * Bare bid/auction rate for the given commodity — gas in `$/therm` precision
 * (`$0.4720`), electric in the configured unit at auction precision
 * (`formatBidRate`). No denominator suffix; the column/label carries it (see
 * `rateUnitLabelFor`). Returns `—` for null/undefined/NaN.
 */
export function formatBidRateFor(
  commodity: Commodity,
  v: Numeric,
  unit?: PricingUnit,
): string {
  if (commodity === "natural_gas") {
    const n = parseNumeric(v);
    if (n == null) return "—";
    const shown = activeGasUnit ? perThermToGasUnit(n, activeGasUnit) : n;
    return `$${shown.toFixed(4)}`;
  }
  return formatBidRate(v, unit);
}

/**
 * The rate-unit label for the given commodity — `$/therm` for gas
 * (invariant: gas never converts), the given electric unit (default: the
 * configured one — `$/kWh` / `¢/kWh` / `$/MWh`) otherwise. Use on a
 * column header / caption that names the unit beside a `formatBidRateFor`
 * value, passing the same unit.
 */
export function rateUnitLabelFor(commodity: Commodity, unit?: PricingUnit): string {
  return commodity === "natural_gas" ? `$/${gasRateDenominator()}` : pricingUnitLabel(unit);
}

/**
 * CCA strike price at 5 decimals with the commodity's delivered-unit
 * suffix — `$0.05500/kWh` for electric, `$0.55000/Ccf` for natural gas
 * (AB#7087). Strike prices are community-aggregation program rates and do
 * NOT ride the configurable pricing unit. Returns `—` for
 * null/undefined/NaN.
 */
export function formatStrikePrice(commodity: Commodity, v: Numeric): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  const unit = commodity === "natural_gas" ? "Ccf" : "kWh";
  return `$${n.toFixed(5)}/${unit}`;
}

/**
 * Rate expressed in cents to one decimal, e.g. `38.2¢`. Input is the
 * `$/unit` value (e.g. `0.382` → `38.2¢`). Returns `—` for
 * null/undefined/NaN. Used on the portfolio-analyzer charts where the
 * cent unit reads more naturally than 4-decimal dollars.
 */
export function formatCents(v: Numeric): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  return `${(n * 100).toFixed(1)}¢`;
}

// ── Volumes (commodity-aware) ─────────────────────────────────────────────────
//
// Wire shape: usage arrives in STORAGE units (kWh for electric, therms
// for gas) and rates arrive in $/storage-unit. Sales-facing surfaces
// display in DISPLAY units (MWh / Dth — what a buyer thinks in). These
// helpers are the single home for that conversion + labeling; do not
// re-derive the 1000 / 10 divisors inline.

/** Storage unit label per commodity — `kWh` (electric) or `therms` (gas). */
export function storageUnit(commodity: Commodity): "kWh" | "therms" {
  return commodity === "electric" ? "kWh" : "therms";
}

/** Display (buyer-facing) unit label per commodity — `MWh`, and `Dth` or
 *  the user's gas unit. */
export function displayUnit(commodity: Commodity): string {
  if (commodity === "electric") return "MWh";
  return activeGasUnit ? GAS_UNIT_LABELS[activeGasUnit] : "Dth";
}

/**
 * Divisor that converts a storage-unit value into its display unit
 * (kWh → MWh ÷ 1000; therms → Dth ÷ 10). The same factor multiplies a
 * `$/storage-unit` rate up to a `$/display-unit` rate, since rate
 * inverts volume.
 */
export function volumeDivisor(commodity: Commodity): number {
  if (commodity === "electric") return 1000;
  return activeGasUnit ? THERMS_PER_GAS_UNIT[activeGasUnit] : 10;
}

/**
 * Which unit family a volume renders in — the sales-facing display units
 * (`MWh` / `Dth`) or the storage units the wire carries (`kWh` / `therms`).
 * The RFQ Results unit toggle flips it for every volume on that page, and
 * the Print Summary export sends the position so the PDF matches. Generated
 * from the Rust `VolumeScale`; re-exported here because every consumer
 * reaches for it alongside the formatters.
 */
export type { VolumeScale };

/** Unit label for a commodity at the given scale. */
export function volumeUnitAt(commodity: Commodity, scale: VolumeScale): string {
  return scale === "display" ? displayUnit(commodity) : storageUnit(commodity);
}

/**
 * Format a storage-unit wire value at the given scale, with no unit suffix —
 * pair it with `volumeUnitAt`. Display scale keeps one decimal because a
 * Dth / MWh figure is an order of magnitude smaller; storage scale is a
 * whole count. Returns `—` for null/undefined/NaN.
 */
export function formatVolumeAt(
  v: Numeric,
  commodity: Commodity,
  scale: VolumeScale,
): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  return scale === "display"
    ? formatDecimal(n / volumeDivisor(commodity))
    : formatNumber(n);
}

/**
 * Format a raw kWh count with compact suffixes — `1.5M kWh`, `45.3k kWh`,
 * `950 kWh`. Used for headline volume tiles where vertical density matters
 * more than full precision. Returns `—` for null/undefined/NaN.
 */
export function formatKwh(v: Numeric): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M kWh`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k kWh`;
  return `${formatNumber(n)} kWh`;
}

/** MWh display string, e.g. `2.5 MWh`. Returns `—` for null/undefined/NaN. */
export function formatMwh(v: Numeric): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  return `${formatDecimal(n)} MWh`;
}

/** A Dth quantity, e.g. `15 Dth` — or in the user's gas unit when they
 *  picked one. Returns `—` for null/undefined/NaN. */
export function formatDth(v: Numeric): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  if (!activeGasUnit) return `${formatDecimal(n)} Dth`;
  return `${formatDecimal(thermsToGasUnit(n * 10, activeGasUnit))} ${GAS_UNIT_LABELS[activeGasUnit]}`;
}

/** A therms quantity, e.g. `150 therms` — or in the user's gas unit when
 *  they picked one. Returns `—` for null/undefined/NaN. */
export function formatTherms(v: Numeric): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  if (!activeGasUnit) return `${formatDecimal(n)} therms`;
  return `${formatDecimal(thermsToGasUnit(n, activeGasUnit))} ${GAS_UNIT_LABELS[activeGasUnit]}`;
}

/**
 * Format a storage-unit value (kWh / therms) into the sales-facing display
 * unit (MWh / Dth) based on the active commodity, with the unit suffix —
 * e.g. `2.5 MWh`, `15.3 Dth`. Returns `—` for null/undefined/NaN.
 */
export function formatVolume(v: Numeric, commodity: Commodity): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  return `${formatDecimal(n / volumeDivisor(commodity))} ${displayUnit(commodity)}`;
}

/**
 * Format a `$/storage-unit` rate into the sales-facing `$/display-unit`
 * rate — e.g. `$230.00/MWh`, `$0.0700/MWh`. Whole-dollar-per-display-unit
 * values render with 2 decimals; sub-dollar values keep 4 so significant
 * digits survive. Returns `—` for null/undefined/NaN.
 */
export function formatVolumeRate(v: Numeric, commodity: Commodity): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  const scaled = n * volumeDivisor(commodity);
  const digits = Math.abs(scaled) >= 1 ? 2 : 4;
  return `$${scaled.toFixed(digits)}/${displayUnit(commodity)}`;
}

/**
 * A contract's own as-quoted headline rate — value + unit are both supplied
 * by the caller (e.g. Contract Analyzer's extracted `{ value, unit }`, unit
 * printed verbatim from the document as "$/MWh", "cents/kWh", etc.), unlike
 * `formatRate`/`formatVolumeRate` which convert a canonical `$/kWh` wire
 * value into a configurable display unit. There's no unit conversion or
 * currency-symbol guessing here — the unit renders exactly as extracted, so
 * "$/MWh" and "cents/kWh" both come through correctly. Whole values ≥ 1
 * render with 2 decimals; sub-1 values keep 4 so significant digits survive
 * (same threshold as `formatVolumeRate`). Returns `—` for a null rate or an
 * unparseable value.
 */
export function formatQuotedRate(rate: { value: Numeric; unit: string } | null | undefined): string {
  if (!rate) return "—";
  const n = parseNumeric(rate.value);
  if (n == null) return "—";
  const digits = Math.abs(n) >= 1 ? 2 : 4;
  return `${n.toFixed(digits)} ${rate.unit}`;
}

// ── Percentages ─────────────────────────────────────────────────────────────

/**
 * Render a value the model intends as a percentage. The bill-analyzer
 * tool schema asks for fractions in `[0, 1]` (e.g. `0.46` = 46%), but
 * the model occasionally returns the already-multiplied form (e.g.
 * `46`). Any value > 1.5 is treated as a literal percentage; values
 * ≤ 1.5 are treated as fractions. The 1.5 cutoff (rather than 1.0)
 * tolerates rates above unity for fields like power factor on
 * capacitor-corrected systems without misreading them.
 *
 * `decimals` controls the rendered precision — pass 0 for whole-number
 * fields like load factor, 1 for power factor. Returns `—` for null,
 * undefined, or NaN.
 */
export function formatPct(v: Numeric, decimals = 0): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  const asPct = n > 1.5 ? n : n * 100;
  return `${asPct.toFixed(decimals)}%`;
}

/**
 * Signed percentage from an already-percentage value (NOT a fraction),
 * e.g. `12.3` → `+12.3%`, `-4` → `−4.0%`, `0` → `0.0%`. Uses the
 * typographic minus sign (U+2212) for negatives to match the rest of
 * the analytics surfaces. `decimals` defaults to 1. Returns `—` for
 * null/undefined/NaN. Use for "vs tariff" / "vs benchmark" deltas.
 */
export function formatPctSigned(v: Numeric, decimals = 1): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  return `${sign}${Math.abs(n).toFixed(decimals)}%`;
}

/**
 * Unsigned percentage from an already-percentage value (NOT a fraction),
 * with trailing zeros trimmed: `25` → `25%`, `12.5` → `12.5%`, `0` →
 * `0%`. Unlike `formatPct` there is no fraction-vs-percentage heuristic —
 * use this for stored percentage columns where `1` means one percent, not
 * one hundred. Returns `—` for null/undefined/NaN.
 */
export function formatPctUnsigned(v: Numeric, maxDecimals = 2): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  return `${new Intl.NumberFormat("en-US", {
    maximumFractionDigits: maxDecimals,
  }).format(n)}%`;
}

/**
 * Signed percentage from a FRACTIONAL gap, e.g. `0.59` → `+59%`,
 * `-0.12` → `−12%`. Multiplies by 100 first; otherwise identical to
 * `formatPctSigned`. `decimals` defaults to 0. Returns `—` for
 * null/undefined/NaN.
 */
export function formatPctGap(v: Numeric, decimals = 0): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  return formatPctSigned(n * 100, decimals);
}

/**
 * English ordinal for a positive integer rank: `1` → `1st`, `2` → `2nd`,
 * `3` → `3rd`, `11` → `11th`, `22` → `22nd`. Returns `—` for
 * null/undefined/NaN. Used for supplier bid ranks ("Ranked 2nd" — the
 * ordinal renders WITHOUT a denominator by design).
 */
export function formatOrdinal(v: Numeric): string {
  const n = parseNumeric(v);
  if (n == null) return "—";
  const i = Math.trunc(n);
  const mod100 = Math.abs(i) % 100;
  const mod10 = Math.abs(i) % 10;
  const suffix =
    mod100 >= 11 && mod100 <= 13
      ? "th"
      : mod10 === 1
        ? "st"
        : mod10 === 2
          ? "nd"
          : mod10 === 3
            ? "rd"
            : "th";
  return `${i}${suffix}`;
}
